import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Modal, Button } from '../common';
import { applyNumberSteps, getFlagDisplay } from './ocrApi';
import { planRenumber, planApplySteps, planReindexByOrder } from './numberPlan';

/**
 * 번호 일괄 다시 매기기 (OcrHub).
 *
 * 시험지가 객관식 1~16 · 서답형 1~5처럼 번호를 두 벌로 매기면, 매칭 결과에서 1~5가 겹친다.
 * 이걸 한 건씩 검수 모달을 열어 고치면 5번 왕복이라, 여기서는 고를 것만 골라 한 번에 옮긴다.
 *
 * 안전 장치 셋:
 *   1. 적용 전에 'from → to' 를 전부 보여주고, 선택 밖 문제와 번호가 겹치면 막는다.
 *   2. 번호가 커지는 이동은 큰 번호부터 처리한다 — 그래야 선택끼리 서로를 밟지 않는다.
 *      (1→17, 2→18 을 오름차순으로 돌리면 중간 상태에서 번호가 겹칠 수 있다)
 *   3. PATCH는 문제당 1회뿐이라 순차로 돌리고, 한 건이라도 실패하면 즉시 멈춘다.
 *      반쯤 적용된 채로 계속 밀어붙이면 어디까지 됐는지 알 수 없게 된다.
 */

// 드래그 자동 스크롤이 움직일 스크롤 컨테이너 — 클래스명이 아니라 계산 스타일로 찾는다.
// 표를 감싸 스크롤하는 것은 공용 Modal의 바디인데, 그 내부 구조에 의존하면 Modal이 바뀔 때 조용히 깨진다.
function findScrollParent(el) {
  let node = el ? el.parentElement : null;
  while (node && node !== document.body) {
    const overflowY = window.getComputedStyle(node).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') return node;
    node = node.parentElement;
  }
  return null;
}

function RenumberModal({ isOpen, items, initialNumber = null, onClose, onApplied, getAccessToken }) {
  const [selected, setSelected] = useState(() => new Set());
  const [startText, setStartText] = useState('');
  const [dupOnly, setDupOnly] = useState(false);
  const [applying, setApplying] = useState(false);
  const [progress, setProgress] = useState(null); // { done, total }
  const [error, setError] = useState(null);
  const [doneMsg, setDoneMsg] = useState(null);

  const list = useMemo(() => (Array.isArray(items) ? items : []), [items]);

  // 번호별 개수 — 중복 판정과 '선택 밖 번호' 충돌 검사에 함께 쓴다
  const numCount = useMemo(() => {
    const m = new Map();
    list.forEach((p) => {
      if (typeof p.problem_number === 'number') m.set(p.problem_number, (m.get(p.problem_number) || 0) + 1);
    });
    return m;
  }, [list]);

  // ── 드래그 선택 상태 ── (핸들러들은 아래 visible 정의 뒤에 있다)
  const dragRef = useRef(null); // { mode, anchor, lastIdx, baseline, ids, pointer, rowsEl, scrollEl, startX, startY }
  const scrollLoopRef = useRef(0);
  const tableWrapRef = useRef(null);

  const endDrag = useCallback(() => {
    dragRef.current = null;
    if (scrollLoopRef.current) {
      cancelAnimationFrame(scrollLoopRef.current);
      scrollLoopRef.current = 0;
    }
  }, []);

  // 지도에서 특정 번호 칸을 눌러 열었으면 그 번호를 미리 골라 둔다(바로 무엇을 옮길지 보이도록)
  useEffect(() => {
    if (!isOpen) return;
    // 목록 정체성이 바뀌면(적용 직후 재조회 등) 드래그 스냅숏(ids·baseline)이 낡는다 —
    // 여기서 끊지 않으면 아래 초기화를 다음 mousemove가 낡은 baseline으로 덮어쓴다
    endDrag();
    setError(null);
    setDoneMsg(null);
    setProgress(null);
    if (initialNumber != null) {
      setDupOnly(true);
      setSelected(new Set(list.filter((p) => p.problem_number === initialNumber).map((p) => p.ocr_prob_id)));
    } else {
      setDupOnly(false);
      setSelected(new Set());
    }
  }, [isOpen, initialNumber, list, endDrag]);

  const visible = dupOnly ? list.filter((p) => (numCount.get(p.problem_number) || 0) > 1) : list;

  const start = startText.trim() === '' ? null : Number(startText);
  const startValid = start != null && Number.isInteger(start) && start > 0;
  // 계획·충돌 판정은 순서 맞추기 화면과 같은 규칙을 써야 한다 → numberPlan 공용
  const { plan, conflicts, changed, selectedList } = useMemo(
    () => planRenumber(list, selected, start),
    [list, selected, start],
  );

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // 전체 선택은 '표시된 것'이 아니라 리스트 전체다(오너 결정 2026-08-23) —
  // 겹침 필터를 켠 채 눌러도 숨은 행까지 함께 골라진다.
  const allSelected = list.length > 0 && list.every((p) => selected.has(p.ocr_prob_id));
  const toggleAll = () => {
    if (applying) return;
    setSelected(allSelected ? new Set() : new Set(list.map((p) => p.ocr_prob_id)));
  };

  // 일부만 선택된 상태를 빈 체크박스로 보여주면 거짓말이다 — 부분 선택은 indeterminate로 말한다.
  // (React는 indeterminate를 속성으로 못 받아 DOM으로 직접 건다)
  const headCbRef = useRef(null);
  useEffect(() => {
    if (headCbRef.current) headCbRef.current.indeterminate = selected.size > 0 && !allSelected;
  }, [selected, allSelected]);

  // ── 드래그 선택 핸들러 ──
  // 행에서 마우스를 누른 채 끌면 시작 행~현재 행의 연속 구간이 시작 행과 같은 방향(선택/해제)으로 바뀐다.
  // 지나간 행을 하나씩 칠하는 방식은 마우스가 빠르면 진입 이벤트가 건너뛰어 구멍이 나므로 구간으로 계산한다.

  // 드래그 시작 시점의 스냅숏(baseline)에서 매번 다시 계산한다 —
  // 그래야 끌다가 되돌아왔을 때 구간 밖으로 나간 행이 원래 상태로 돌아온다.
  const applyDragRange = useCallback((idx) => {
    const d = dragRef.current;
    if (!d || idx == null || idx === d.lastIdx) return;
    d.lastIdx = idx;
    const next = new Set(d.baseline);
    const a = Math.min(d.anchor, idx);
    const b = Math.max(d.anchor, idx);
    for (let i = a; i <= b; i += 1) {
      if (d.mode === 'add') next.add(d.ids[i]);
      else next.delete(d.ids[i]);
    }
    setSelected(next);
  }, []);

  // 포인터 아래 행 찾기. 행 영역(tbody) 위/아래로 벗어나면 첫/끝 행으로 잡아 끝까지 끌 수 있게 하고,
  // 좌우로 벗어나면 x를 표 안쪽으로 사상해 세로 추적이 끊기지 않게 한다.
  // 기준을 표 전체가 아니라 tbody로 잡는 이유: 머리행 위에 포인터가 있을 때도 '맨 위로'로 읽혀야 한다.
  const dragIndexAtPoint = useCallback((x, y) => {
    const d = dragRef.current;
    if (!d || !d.rowsEl) return null;
    const rect = d.rowsEl.getBoundingClientRect();
    if (y < rect.top) return 0;
    if (y > rect.bottom) return d.ids.length - 1;
    const cx = Math.min(Math.max(x, rect.left + 1), rect.right - 1);
    // tbody rect는 스크롤 클리핑을 모른다 — 스크롤 중이면 모달 헤더·푸터 뒤까지 뻗어,
    // 그 위의 포인터가 사각지대에 빠진다. y도 '보이는 행 영역' 안으로 사상해 항상 행을 잡는다.
    let top = rect.top;
    let bottom = rect.bottom;
    if (d.scrollEl) {
      const sr = d.scrollEl.getBoundingClientRect();
      top = Math.max(top, sr.top);
      bottom = Math.min(bottom, sr.bottom);
    }
    const cy = Math.min(Math.max(y, top + 1), bottom - 1);
    const el = document.elementFromPoint(cx, cy);
    const row = el && el.closest ? el.closest('tr[data-drag-idx]') : null;
    return row ? Number(row.dataset.dragIdx) : null;
  }, []);

  // 가장자리 자동 스크롤 — 포인터가 멈춰 있으면 마우스 이벤트가 안 오므로 프레임 루프가 대신 민다.
  // 스크롤로 포인터 아래 행이 바뀌어도 브라우저는 이벤트를 주지 않아, 민 다음 직접 다시 계산한다.
  const runAutoScroll = useCallback(() => {
    const d = dragRef.current;
    if (!d) return;
    const sc = d.scrollEl;
    if (sc && d.pointer) {
      const rect = sc.getBoundingClientRect();
      const EDGE = 32; // 행 높이 한 줄 남짓 — 이 안에 들어오면 밀기 시작
      let dy = 0;
      if (d.pointer.y < rect.top + EDGE) dy = -Math.min(24, Math.ceil((rect.top + EDGE - d.pointer.y) / 4));
      else if (d.pointer.y > rect.bottom - EDGE) dy = Math.min(24, Math.ceil((d.pointer.y - (rect.bottom - EDGE)) / 4));
      if (dy !== 0) {
        const before = sc.scrollTop;
        sc.scrollTop = before + dy;
        if (sc.scrollTop !== before) applyDragRange(dragIndexAtPoint(d.pointer.x, d.pointer.y));
      }
    }
    scrollLoopRef.current = requestAnimationFrame(runAutoScroll);
  }, [applyDragRange, dragIndexAtPoint]);

  const handleRowMouseDown = (e, idx, id) => {
    if (applying || e.button !== 0) return;
    e.preventDefault(); // 끄는 동안 글자가 파랗게 선택되는 것을 막는다
    const wrap = tableWrapRef.current;
    dragRef.current = {
      mode: selected.has(id) ? 'remove' : 'add',
      anchor: idx,
      lastIdx: null,
      baseline: new Set(selected),
      ids: visible.map((p) => p.ocr_prob_id), // 드래그 중에는 필터가 못 바뀌므로 스냅숏으로 충분
      pointer: { x: e.clientX, y: e.clientY },
      startX: e.clientX,
      startY: e.clientY,
      rowsEl: wrap ? wrap.querySelector('tbody') : null,
      scrollEl: findScrollParent(wrap),
    };
    applyDragRange(idx); // 누른 행부터 즉시 토글 — 클릭 한 번의 기존 동작이 그대로 유지된다
    // 자동 스크롤 루프는 여기서 켜지 않는다 — 가장자리 행을 그냥 클릭해도 누른 순간부터
    // 목록이 밀려 옆 행까지 선택되기 때문. 포인터가 실제로 움직인 뒤(onMove)에만 켠다.
  };

  // 드래그는 행 밖·모달 밖에서 끝날 수 있어 창 전체에서 듣는다
  useEffect(() => {
    if (!isOpen) return undefined;
    const onMove = (e) => {
      const d = dragRef.current;
      if (!d) return;
      // Cmd-Tab 등으로 창 밖에서 버튼을 놓으면 mouseup이 오지 않는다 —
      // 주 버튼이 떼어진 채 오는 mousemove를 드래그 종료로 읽는다
      if ((e.buttons & 1) === 0) {
        endDrag();
        return;
      }
      d.pointer = { x: e.clientX, y: e.clientY };
      // 4px 문턱을 넘은 진짜 드래그에서만 자동 스크롤을 켠다(제자리 클릭 보호)
      if (
        !scrollLoopRef.current &&
        (Math.abs(e.clientX - d.startX) > 4 || Math.abs(e.clientY - d.startY) > 4)
      ) {
        scrollLoopRef.current = requestAnimationFrame(runAutoScroll);
      }
      applyDragRange(dragIndexAtPoint(e.clientX, e.clientY));
    };
    const onUp = () => endDrag();
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('blur', onUp); // 포커스를 잃으면 즉시 종료 — 자동 스크롤 잔존 방지
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('blur', onUp);
      endDrag();
    };
  }, [isOpen, applyDragRange, dragIndexAtPoint, endDrag, runAutoScroll]);

  const handleApply = async () => {
    if (applying || changed.length === 0 || conflicts.length > 0) return;
    endDrag(); // 키보드(Enter)로 발동될 수 있다 — 드래그가 살아 있으면 적용 후 초기화를 덮어쓴다
    setApplying(true);
    setError(null);
    setDoneMsg(null);
    await runSteps(changed, `${changed.length}건의 인덱스를 옮겼습니다.`);
  };

  // 계획을 실제로 적용하는 공통 경로 — '선택한 것 옮기기'와 '원본 순서대로 1부터'가 같은 규칙을 쓴다
  const runSteps = async (rows, successMsg) => {
    // 목표 자리가 빈 것부터 옮기고, 서로 물고 도는 경우에는 임시 번호를 거쳐 고리를 끊는다
    const steps = planApplySteps(list, rows);
    setProgress({ done: 0, total: steps.length });
    try {
      const res = await applyNumberSteps(steps, getAccessToken, (done, total) => setProgress({ done, total }));
      if (res.aborted) return; // 토큰 부재/401 → /signin 리다이렉트 중
      setDoneMsg(successMsg);
      setSelected(new Set());
      setStartText('');
      onApplied && onApplied();
    } catch (e) {
      // 중간에 멈춘 지점을 정확히 알린다 — '몇 건까지 됐는지'를 모르면 되돌릴 수도 이어갈 수도 없다
      setError(`적용 도중 멈췄습니다 — ${e.message || '인덱스 수정에 실패했습니다.'} 목록을 새로 읽고 다시 시도하세요.`);
      onApplied && onApplied();
    } finally {
      setApplying(false);
    }
  };

  // 원본 등장 순서대로 1부터 — 선택과 무관하게 프로젝트 전체를 다시 매긴다
  const reindex = planReindexByOrder(list);
  const handleReindex = async () => {
    if (applying || reindex.blocked || reindex.changed.length === 0) return;
    endDrag(); // handleApply와 같은 이유
    setApplying(true);
    setError(null);
    setDoneMsg(null);
    await runSteps(reindex.changed, `원본 순서대로 ${reindex.changed.length}건의 인덱스를 정리했습니다.`);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={applying ? () => {} : onClose}
      size="xl"
      maxHeight="88vh"
      fixedHeight // 겹침 필터를 켜고 끌 때 모달 세로 크기가 널뛰지 않게 내용량과 무관하게 고정
      title="인덱스 다시 매기기"
      footer={
        <div className="flex w-full items-center gap-3">
          <div className="min-w-0 flex-1 text-xs">
            {error && <span className="text-red-600">{error}</span>}
            {!error && doneMsg && <span className="text-blue-600">{doneMsg}</span>}
            {!error && !doneMsg && conflicts.length > 0 && (
              <span className="text-red-600">
                {conflicts.map((r) => r.to).join(', ')}번은 선택하지 않은 문제가 쓰고 있습니다 — 그 문제도 함께 고르거나 시작 인덱스를 바꾸세요
              </span>
            )}
            {!error && !doneMsg && conflicts.length === 0 && changed.length > 0 && (
              <span className="text-gray-500">충돌 없음 · {changed.length}건이 바뀝니다</span>
            )}
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={applying}>
            닫기
          </Button>
          <Button
            size="sm"
            onClick={handleApply}
            loading={applying}
            disabled={applying || changed.length === 0 || conflicts.length > 0}
          >
            {applying && progress ? `적용 중 ${progress.done}/${progress.total}단계` : `${changed.length}건 적용`}
          </Button>
        </div>
      }
    >
      {/* 원본 순서대로 1부터 — 대개 이것 한 번이면 끝난다. 아래 '골라서 옮기기'는 예외 처리용 */}
      <div className="mb-3 flex flex-wrap items-center gap-2 rounded-2xl border border-primary/30 bg-primary/5 p-3">
        <span className="text-sm font-medium text-gray-800">원본 순서대로 1부터</span>
        <span className="text-xs text-gray-500">
          {reindex.blocked === 'promoted'
            ? `등록된 문제 ${reindex.promoted.length}건이 있어 할 수 없습니다 — 등록된 문제의 인덱스는 바꿀 수 없습니다`
            : reindex.changed.length === 0
              ? '이미 원본 순서대로 매겨져 있습니다'
              : `${reindex.changed.length}건이 바뀝니다`}
          {reindex.splitCount > 0 && reindex.blocked == null && ` · 충돌 분리 ${reindex.splitCount}건은 맨 뒤로 밀립니다`}
        </span>
        <Button
          size="xs"
          className="ml-auto"
          onClick={handleReindex}
          disabled={applying || !!reindex.blocked || reindex.changed.length === 0}
        >
          정리
        </Button>
      </div>

      {/* 골라서 옮기기 — 시작 인덱스 + 미리보기 */}
      <div className="mb-3 rounded-2xl border border-gray-200 bg-gray-50 p-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-gray-700">선택 {selectedList.length}건</span>
          <span className="text-xs text-gray-400">책 순서대로 연속 부여</span>
          <label className="ml-auto flex items-center gap-1.5 text-sm text-gray-700" htmlFor="renumber-start">
            시작 인덱스
            <input
              id="renumber-start"
              type="text"
              inputMode="numeric"
              value={startText}
              onChange={(e) => setStartText(e.target.value.replace(/[^0-9]/g, ''))}
              disabled={applying}
              placeholder="예: 17"
              className="w-24 rounded-xl border px-3 py-1.5 text-sm focus:border-primary focus:outline-none"
            />
          </label>
        </div>

        {selectedList.length > 0 && !startValid && (
          <p className="mt-2 text-xs text-gray-400">시작 인덱스를 넣으면 바뀔 인덱스를 미리 보여줍니다.</p>
        )}
        {plan.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
            {plan.map((r) => (
              <span
                key={r.p.ocr_prob_id}
                className={`text-xs ${
                  conflicts.includes(r) ? 'font-bold text-red-600' : r.to === r.from ? 'text-gray-400' : 'text-gray-700'
                }`}
              >
                {r.from} → <span className="font-medium">{r.to}</span>
                {r.to === r.from && ' (그대로)'}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* 대상 고르기 */}
      <div className="mb-2 flex items-center gap-2">
        <button
          type="button"
          onClick={() => {
            endDrag(); // 드래그 중 키보드(Space) 토글로 표시 목록과 드래그 스냅숏이 어긋나는 것을 막는다
            setDupOnly((v) => !v);
          }}
          disabled={applying}
          className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
            dupOnly ? 'bg-primary/10 font-bold text-primary' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
          }`}
        >
          인덱스가 겹친 것만
        </button>
        <span className="ml-auto text-xs text-gray-400">{visible.length}건 표시</span>
      </div>

      <div ref={tableWrapRef} className="overflow-hidden rounded-xl border">
        <table className="w-full">
          {/* 머리행 디자인은 형제 표(OcrProblemList)와 동일 — bg-gray-50 · 작은 회색 라벨 */}
          <thead className="bg-gray-50">
            <tr>
              <th className="w-10 px-3 py-2 text-left">
                <input
                  ref={headCbRef}
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  disabled={applying || list.length === 0}
                  title={allSelected ? '전체 해제' : '전체 선택'}
                  aria-label={allSelected ? '전체 해제' : '전체 선택'}
                />
              </th>
              <th className="w-16 px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                인덱스
              </th>
              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500">본문</th>
              <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                플래그
              </th>
            </tr>
          </thead>
          <tbody className="bg-white">
            {visible.map((p, i) => {
              const dup = (numCount.get(p.problem_number) || 0) > 1;
              const on = selected.has(p.ocr_prob_id);
              return (
                <tr
                  key={p.ocr_prob_id}
                  data-drag-idx={i}
                  onMouseDown={(e) => handleRowMouseDown(e, i, p.ocr_prob_id)}
                  className={`cursor-pointer border-b border-gray-100 transition-colors last:border-b-0 ${
                    on ? 'bg-primary/10' : 'hover:bg-gray-50'
                  }`}
                >
                  <td className="w-10 px-3 py-2">
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => toggle(p.ocr_prob_id)}
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      disabled={applying}
                      aria-label={`문제 ${p.problem_number} 선택`}
                    />
                  </td>
                  <td className="w-16 whitespace-nowrap px-3 py-2">
                    <span className={`text-sm font-medium ${dup ? 'text-red-600' : 'text-gray-900'}`}>
                      {p.problem_number}
                    </span>
                    {dup && <span className="ml-1 text-[10px] text-red-500">겹침</span>}
                  </td>
                  <td className="px-3 py-2">
                    <div className="max-w-[420px] truncate text-xs text-gray-500" title={p.body_preview || undefined}>
                      {p.body_preview || <span className="text-gray-300">본문 미리보기 없음</span>}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <span className="flex flex-wrap justify-end gap-1">
                      {(Array.isArray(p.flags) ? p.flags : []).map((code) => {
                        const fi = getFlagDisplay(code);
                        return (
                          <span
                            key={code}
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${fi.cls}`}
                          >
                            {fi.text}
                          </span>
                        );
                      })}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

export default RenumberModal;
